import React, {useCallback, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {
  AUTH_LOGIN_MAX_ATTEMPT,
  AUTH_USERNAME_MAX_LENGTH,
  AUTHENTICATOR_OTP_LENGTH,
  REGEX_ALPHANUMERIC,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {formatCooldownTime, formatCooldownTimeToString} from 'utils'
import {
  SettingAuthenticatorInputScreenType,
  SettingAuthenticatorRecoveryInput,
} from 'types'
import {useWindowMode} from 'windows'
import {Button, Input, InputOTP, Paragraph} from 'common/components'
import {VALIDATION_GIFTSHOP_AUTHENTICATOR_RECOVERY} from 'common/validations'
import convertUnit from 'lib/unit'
import {useDispatch} from 'lib/redux'
import {GiftShopEditProfileLimitPasswordModal} from '../../../edit-profile'
import {GiftShopSettingAuthenticatorSetupProps} from './GiftShopSettingAuthenticatorSetup'

const StyledContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-self: center;
  background-color: ${({theme}) => theme.white_3};
`
const StyledContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  border-radius: ${convertUnit(16)};
  max-width: ${convertUnit(560)};
  margin-top: ${convertUnit(20)};
  height: fit-content;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    min-width: 100%;
    height: 100%;
    border-radius: 0;
    margin-top: 0;
  }
  background-color: ${({theme}) => theme.white_1};
`
const StyledSubtitle = styled(Paragraph)`
  text-align: center;
`
const StyledBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 ${convertUnit(20)};
  overflow-y: scroll;
  flex-grow: 1;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    width: 0;
  }
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
`
const StyledTitle = styled(Paragraph)`
  margin: ${convertUnit(16)} 0 ${convertUnit(12)} 0;
  text-align: center;
`
const StyledInputContainer = styled.div`
  margin: ${convertUnit(20)} 0;
`
const StyledFooter = styled.div`
  margin-bottom: ${convertUnit(20)};
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: ${convertUnit(4)};
  padding: 0 ${convertUnit(20)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    border-radius: 0;
  }
`
const StyledRecoveryInputContainer = styled.div`
  width: 100%;
  margin: ${convertUnit(20)} 0;
`

export default function GiftShopSettingAuthenticatorSetupInput({
  stateScreen,
}: GiftShopSettingAuthenticatorSetupProps) {
  const {translate} = useTranslation()
  const {update} = useDispatch()
  const mode = useWindowMode()
  const [codes, setCodes] = useState('')
  const stateError = useState('')
  const [error, setError] = stateError
  const setScreen = stateScreen[1]
  const [loading, setLoading] = useState(false)
  const [cooldownTimer, setCooldownTimer] = useState('0s')
  const [limitModal, setLimitModal] = useState(false)
  const [
    inputScreen,
    setInputScreen,
  ] = useState<SettingAuthenticatorInputScreenType>('recovery')
  const form = useForm<SettingAuthenticatorRecoveryInput>({
    defaultValues: {answer: ''},
    reValidateMode: 'onChange',
  })
  const {watch, errors} = form
  const {answer} = watch()
  const [ans, setAns] = useState('')

  const handleRenderModalLimit = useMemo(
    () =>
      limitModal && (
        <GiftShopEditProfileLimitPasswordModal
          cooldownTimer={cooldownTimer}
          onConfirm={() => {
            setLimitModal(false)
            history.back()
          }}
          title={translate('giftShop:attemptLimitExceeded')}
        />
      ),
    [cooldownTimer, limitModal, translate],
  )
  const handleCheckCode = useCallback(() => {
    setLoading(true)
    requestData('mfa_activate', {
      data: {code: codes, security_answer: ans},
      onRequestReceived: () => setLoading(false),
      onRequestSuccess: ({status, data: {detail}}) => {
        if (status === 200) {
          update('settingState', {isAuthenticatorEnabled: true})
          setAns('')
          setScreen('success')
        } else if (status === 400 && detail && detail.mfa_attempt) {
          setError(
            translate('giftShop:settingAuthAppInvalidError', {
              count: AUTH_LOGIN_MAX_ATTEMPT - parseInt(detail.mfa_attempt, 10),
            }),
          )
        } else if (status === 423 && detail && detail.suspend_duration) {
          update('settingState', {
            mfa_suspend_time: new Date().getTime(),
            mfa_suspend_duration: formatCooldownTime(detail.suspend_duration),
          })
          setCooldownTimer(
            formatCooldownTimeToString(detail.suspend_duration, false, true),
          )
          setLimitModal(true)
        } else {
          setError(
            translate('global:messageError', {context: `code-${status}`}),
          )
        }
      },
    })
  }, [ans, codes, setError, setScreen, translate, update])

  const handleRenderInput = useMemo(
    () => (
      <StyledInputContainer>
        <InputOTP
          boxes={AUTHENTICATOR_OTP_LENGTH}
          onSubmit={(code) => setCodes(code)}
          containerStyle={{
            height: mode === 'mobile' ? 40 : 50,
            width: mode === 'mobile' ? 40 : 50,
          }}
          stateError={stateError}
        />
        {error && (
          <Paragraph fontSize="m" fontWeight="medium" color="danger_5">
            {error}
          </Paragraph>
        )}
      </StyledInputContainer>
    ),
    [error, mode, stateError],
  )

  const handleRenderRecoveryQuestion = useMemo(
    () => (
      <StyledRecoveryInputContainer>
        <Input
          name="answer"
          form={form}
          formRules={VALIDATION_GIFTSHOP_AUTHENTICATOR_RECOVERY}
          autoComplete={false}
          allowedCharacters={REGEX_ALPHANUMERIC}
          maxLength={AUTH_USERNAME_MAX_LENGTH}
          rightIcon="delete"
          label={translate('giftShop:settingAuthAppRecoveryQuestion')}
          labelDescription={translate(
            'giftShop:settingAuthAppRecoveryQuestionHint',
          )}
          labelDescriptionWeight="medium"
          labelDescriptionColor="gray_5"
          labelDescriptionDirection="column"
          placeholder={translate('giftShop:settingAuthAppRecoveryPlaceholder')}
          contentContainerStyle={{width: 'fill-available'}}
        />
      </StyledRecoveryInputContainer>
    ),
    [form, translate],
  )

  const handleRenderScreen = useMemo(() => {
    switch (inputScreen) {
      case 'totp':
        return handleRenderInput
      default:
        return handleRenderRecoveryQuestion
    }
  }, [handleRenderInput, handleRenderRecoveryQuestion, inputScreen])

  return (
    <StyledContainer>
      {handleRenderModalLimit}
      <StyledContentContainer>
        <StyledBodyContainer>
          <StyledTitle fontSize="xl" fontWeight="bold">
            {translate(
              inputScreen === 'totp'
                ? 'giftShop:settingAuthAppInputTitle'
                : 'giftShop:settingAuthAppRecoverySetupTitle',
            )}
          </StyledTitle>
          <StyledSubtitle fontSize="m" color="gray_5">
            {translate(
              inputScreen === 'totp'
                ? 'giftShop:settingAuthAppInputDesc'
                : 'giftShop:settingAuthAppRecoverySetupDesc',
              {
                length: AUTHENTICATOR_OTP_LENGTH,
              },
            )}
          </StyledSubtitle>
          {handleRenderScreen}
        </StyledBodyContainer>
        <StyledFooter>
          <Paragraph fontSize="m" color="gray_5">
            {translate('global:step')
              .concat(' ')
              .concat(
                translate('giftShop:xOfy', {
                  x: inputScreen === 'totp' ? 3 : 2,
                  y: 3,
                }),
              )}
          </Paragraph>
          <Button
            label={translate(
              inputScreen === 'totp' ? 'global:verify' : 'global:save',
            )}
            disabled={
              inputScreen === 'totp'
                ? codes.length < AUTHENTICATOR_OTP_LENGTH
                : answer === '' || !!errors.answer
            }
            onClick={() => {
              if (inputScreen === 'totp') {
                handleCheckCode()
              } else {
                setAns(answer)
                setInputScreen('totp')
              }
            }}
            isLoading={loading}
          />
        </StyledFooter>
      </StyledContentContainer>
    </StyledContainer>
  )
}
